'use client';

import { useEffect, useState, type CSSProperties, type PointerEvent } from 'react';

import type { ProductListItem } from '@/api/backend/contract/ProductList';
import { getProductListRelated } from '@/api/backend/service/getProductList';
import { FeatureProductTile } from '@/feature/product-tile';
import { storeUiModal } from '@/store-ui/modal';
import { UiKitCarousel } from '@/ui-kit/carousel';
import { UiKitLoader } from '@/ui-kit/loader';

import { styles } from './AddToCartRecommendations.styles';

export interface AddToCartRecommendationsProps {
  title: string;
  visible: boolean;
  sku: string;
  excludeProductUuid?: string;
}

const RECOMMENDATIONS_LIMIT = 12;

const carouselStyle = {
  '--slide-spacing': '12px',
  '--mobile-slide-size': 'calc(171px + var(--slide-spacing))',
  '--desktop-slide-size': 'calc(184px + var(--slide-spacing))'
} as CSSProperties & Record<string, string>;

export function AddToCartRecommendations({
  title,
  visible,
  sku,
  excludeProductUuid
}: AddToCartRecommendationsProps) {
  const slots = styles();
  const [products, setProducts] = useState<ProductListItem[]>([]);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    if (!visible || !sku) return;

    let cancelled = false;
    setProducts([]);
    setIsLoaded(false);

    getProductListRelated({ sku, params: { limit: RECOMMENDATIONS_LIMIT } }).then(
      (result) => {
        if (cancelled) return;
        if (!result.isError && result.data?.items?.length) {
          setProducts(
            result.data.items.filter((item) => item.uuid !== excludeProductUuid)
          );
        }
        setIsLoaded(true);
      }
    );

    return () => {
      cancelled = true;
    };
  }, [visible, sku, excludeProductUuid]);

  const isLoading = visible && Boolean(sku) && !isLoaded;
  const showBlock = visible && (isLoading || products.length > 0);

  const stopDrawerDrag = (event: PointerEvent<HTMLDivElement>) => {
    event.stopPropagation();
  };

  return (
    <div
      className={slots.collapse({ visible: showBlock })}
      aria-hidden={!showBlock}
    >
      <div className={slots.collapseInner()}>
        <div className={slots.block()}>
          {isLoading ? (
            <>
              <div className={slots.header()}>
                <h3 className={slots.title()}>{title}</h3>
              </div>
              <div className={slots.loader()}>
                <UiKitLoader />
              </div>
            </>
          ) : (
            <div
              className={slots.carouselWrapper()}
              data-vaul-no-drag=""
              onPointerDownCapture={stopDrawerDrag}
            >
              <UiKitCarousel
                className={slots.carousel()}
                arrowType="chevron"
                arrowNextClass={slots.arrow()}
                arrowPrevClass={slots.arrow()}
                renderButtons={(buttons) => (
                  <div className={slots.header()}>
                    <h3 className={slots.title()}>{title}</h3>
                    <div className={slots.arrows()}>{buttons}</div>
                  </div>
                )}
                viewportClassName={slots.viewport()}
                loop={false}
                dragFree
                showArrows="always"
                style={carouselStyle}
              >
                {products.map((product, index) => (
                  <div
                    key={product.id}
                    className={slots.tile()}
                  >
                    <FeatureProductTile
                      product={product}
                      size="l"
                      location="slider"
                      gtmViewConfig={{
                        itemListName: 'add-to-cart-recommendations',
                        position: index + 1
                      }}
                      onProductClick={() => {
                        storeUiModal.getState().close('productModalVariant');
                      }}
                    />
                  </div>
                ))}
              </UiKitCarousel>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
